<template>
    <div class="analytics-overview">
        <header class="overview-header">
            <div class="content">
                <div class="title-wrap">
                    <h3 class="title">
                        {{ title }}
                    </h3>
                    <base-select
                        v-if="!!showSelectOptions"
                        :options="showSelectOptions"
                        initial-value="all"
                        @change="handleShowChange"
                    />
                </div>
                <p v-if="!!description" class="description">
                    {{ description }}
                </p>
            </div>
            <div class="controls">
                <range-buttons
                    @change="handleRangeChange"
                />
                <div v-if="totalListens !== null" class="total-listens">
                    <span class="total-listens-label">Listens:</span>
                    <span class="total-listens-value">{{ totalListens }}</span>
                </div>
            </div>
        </header>
        <data-loader :data="chartData">
            <div class="chart-container">
                <div
                    ref="chartWrap"
                    class="chart-wrap"
                >
                    <bar-chart
                        :data="chartData"
                        :time-unit="bucket"
                    />
                </div>
            </div>
        </data-loader>
    </div>
</template>

<script>

/**
 * External dependencies
 */
import { chain } from 'lodash';
import { mapActions, mapState } from 'vuex';

/**
 * Internal dependencies
 */
import analyticsData from '@/mixins/analyticsData';
import BarChart from './Charts/BarChart';
import DataLoader from '@/components/DataLoader';
import numberFormat from '@/utils/numberFormat';
import RangeButtons from './RangeButtons';

export default {
    components: {
        BarChart,
        DataLoader,
        RangeButtons,
    },

    mixins: [
        analyticsData,
    ],

    props: {
        description: {
            type: String,
            default: null,
        },
        currentShowHash: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: 'Overview',
        },
    },

    data: () => ({
        chartWidth: 0,
        from: null,
        showHash: null,
        bucket: 'month',
        to: null,
        watchProp: '$data',
    }),

    computed: {
        ...mapState({
            analyticsData: 'listens',
            shows: 'shows',
        }),

        /**
         * Returns top episodes.
         *
         * @returns {Array|null} Top episodes.
         */
        totalListens() {
            return this.analyticsData?.[this.key]
                ? numberFormat(this.analyticsData?.[this.key].reduce(
                    (acc, item) => acc + parseInt(item.totalListens),
                    0
                ))
                : null;
        },

        /**
         * Returns currently selected show.
         *
         * @returns {object|null} Current show or null if not selected.
         */
        currentShow() {
            return this.shows?.[this.currentShowHash || this.showHash] || null;
        },

        /**
         * Returns options array for shows select control.
         *
         * @returns {Array} Select options.
         */
        showSelectOptions() {
            return ! this.currentShowHash && this.shows
                ? [
                    {
                        value: 'all',
                        label: 'All Shows',
                    },
                    ...chain(this.shows)
                        .pickBy(show => show.hosted)
                        .map(show => ({
                            value: show.hash,
                            label: show.title,
                        }))
                        .values()
                        .value(),
                ]
                : null;
        },

        /**
         * Returns time range options prepared for RangeButtons component.
         *
         * @returns {Array} Time range options.
         */
        timeRangeOptions() {
            return Object.entries(this.timeRanges).map(([key, item]) => ({
                value: key,
                label: item.label,
            }));
        },
    },

    watch: {
        bucket: {
            /**
             * Sets limit as analiticsDataParams item.
             *
             * @returns {void}
             */
            handler() {
                this.analyticsDataParams = { bucket: `1 ${this.bucket}` };
            },

            immediate: true,
        },

        currentShowHash: {
            /**
             * Stores current show has from props into data showHash.
             *
             * @returns {void}
             */
            handler() {
                if (this.currentShowHash) {
                    this.showHash = this.currentShowHash;
                }
            },

            immediate: true,
        },
    },

    /**
     * Sets the chart width, registers resize event handler..
     *
     * @returns {void}
     */
    mounted() {
        window.addEventListener('resize', this.updateChartWidth);
    },

    /**
     * Removes resize event handler.
     *
     * @returns {void}
     */
    beforeDestroy() {
        window.removeEventListener('resize', this.updateChartWidth);
    },

    methods: {
        ...mapActions({
            loadData: 'loadListens',
        }),

        /**
         * Updates chart width.
         *
         * @returns {void}
         */
        updateChartWidth() {
            if (! this.$refs.chartWrap) {
                return;
            }

            this.chartWidth = this.$refs.chartWrap.clientWidth;
        },

        /**
         * Handles show change.
         *
         * @param  {string} showHash Show hash.
         * @returns {void}
         */
        handleShowChange(showHash) {
            this.showHash = 'all' === showHash ? null : showHash;

            this.$emit('showChange', this.showHash);
        },

        /**
         * Sets time range.
         *
         * @param {string} params Time range.
         * @returns {void}
         */
        handleRangeChange({ bucket, from, to }) {
            this.bucket = bucket;
            this.from = from;
            this.to = to;

            this.$emit('rangeChange', { from, to });
        },
    },
};
</script>
